/**
 * Auto-configuration utilities for vessel settings
 * Handles cascading equipment requirements based on vessel configuration
 */

import { vesselTypePresets, vesselTypeOperations } from './vesselTypePresets';
import { parseCategory } from '../../../../../utils/categoryHelpers';

// Re-export for backward compatibility
export { vesselTypeOperations };

// Handle engine position change with auto-requirements
export const handleEnginePositionChange = (enginePosition, vesselConfig, updateConfig) => {
  const updates = { enginePosition };
  let currentOptional = [...(vesselConfig.optionalEquipment || [])];
  
  if (enginePosition === 'below_deck') {
    if (!currentOptional.includes('fixed_fire_system')) {
      currentOptional.push('fixed_fire_system');
      updates.autoRequiredFireSystem = true;
    }
  } else if (enginePosition === 'above_deck') {
    if (vesselConfig.autoRequiredFireSystem) {
      currentOptional = currentOptional.filter(eq => eq !== 'fixed_fire_system');
      updates.autoRequiredFireSystem = false;
    }
  }
  
  if (currentOptional.length !== (vesselConfig.optionalEquipment || []).length || 
      currentOptional.some((eq, idx) => eq !== (vesselConfig.optionalEquipment || [])[idx])) {
    updates.optionalEquipment = currentOptional;
  }
  
  updateConfig(updates);
};

export const handlePassengerCapacityChange = (passengerCapacity, vesselConfig, updateConfig) => {
  const updates = { passengerCapacity };
  let currentOptional = [...(vesselConfig.optionalEquipment || [])];
  const passengers = parseInt(passengerCapacity) || 0;
  
  if (passengers > 0 && vesselConfig.requiresPassengerInput) {
    updates.requiresPassengerInput = false;
    updates.passengerInputReminder = null;
  }
  
  if (passengers > 0) {
    if (!currentOptional.includes('pa_system')) {
      currentOptional.push('pa_system');
      updates.autoRequiredPASystem = true;
    }
    
    const crew = parseInt(vesselConfig.crewCount) || 3;
    const totalPersons = passengers + crew;
    if (!vesselConfig.maxPersons || vesselConfig.maxPersons < totalPersons) {
      updates.maxPersons = totalPersons;
      if (totalPersons >= 16) {
        updates.autoRequiredGeneralAlarm = true;
      }
    }
  } else {
    if (vesselConfig.autoRequiredPASystem && currentOptional.includes('pa_system')) {
      const maxPersons = parseInt(vesselConfig.maxPersons) || 0;
      const enginePower = parseFloat(vesselConfig.enginePower) || 0;
      
      if (maxPersons < 16 && enginePower <= 750) {
        currentOptional = currentOptional.filter(eq => eq !== 'pa_system');
        updates.autoRequiredPASystem = false;
        updates.autoRequiredGeneralAlarm = false;
      }
    }
  }
  
  if (passengers > 0) {
    const currentAuto = vesselConfig.autoRequiredEquipment || [];
    if (!currentAuto.includes('ais')) {
      updates.autoRequiredEquipment = [...currentAuto, 'ais'];
    }
  } else {
    const tonnage = parseFloat(vesselConfig.grossTonnage) || 0;
    if (tonnage < 300) {
      const currentAuto = vesselConfig.autoRequiredEquipment || [];
      updates.autoRequiredEquipment = currentAuto.filter(eq => eq !== 'ais');
    }
  }
  
  // Update optional equipment if changed
  if (currentOptional.length !== (vesselConfig.optionalEquipment || []).length ||
      currentOptional.some((eq, idx) => eq !== (vesselConfig.optionalEquipment || [])[idx])) {
    updates.optionalEquipment = currentOptional;
  }
  
  updateConfig(updates);
};

export const handleOperatingHoursChange = (operatingHours, vesselConfig, updateConfig) => {
  const updates = { operatingHours };
  let currentOptional = [...(vesselConfig.optionalEquipment || [])];
  
  if (operatingHours === 'over_24h') {
    const accommodationEquipment = ['hvac', 'galley_equipment', 'watermaker'];
    
    let added = false;
    accommodationEquipment.forEach(eq => {
      if (!currentOptional.includes(eq)) {
        currentOptional.push(eq);
        added = true;
      }
    });
    
    if (added) {
      updates.autoRequiredAccommodation = true;
    }
    
    const maxPersons = parseInt(vesselConfig.maxPersons) || 0;
    if (maxPersons > 4 && !currentOptional.includes('sewage_treatment')) {
      currentOptional.push('sewage_treatment');
      updates.autoRequiredSewage = true;
    }
  } else if (operatingHours === 'under_24h') {
    if (vesselConfig.autoRequiredAccommodation) {
      const accommodationEquipment = ['hvac', 'galley_equipment', 'watermaker'];
      currentOptional = currentOptional.filter(eq => !accommodationEquipment.includes(eq));
      updates.autoRequiredAccommodation = false;
    }
  }
  
  // Update optional equipment if changed
  if (currentOptional.length !== (vesselConfig.optionalEquipment || []).length) {
    updates.optionalEquipment = currentOptional;
  }
  
  updateConfig(updates);
};

export const handleEnginePowerChange = (enginePower, vesselConfig, updateConfig) => {
  const updates = { enginePower };
  let currentOptional = [...(vesselConfig.optionalEquipment || [])];
  const power = parseFloat(enginePower) || 0;
  
  // Handle General Alarm based on engine power
  if (power > 750) {
    // Auto-require general alarm for vessels with >750kW installed power
    if (!currentOptional.includes('general_alarm')) {
      currentOptional.push('general_alarm');
      updates.autoRequiredGeneralAlarm = true;
    }
  } else {
    // Remove GA if it was auto-added for engine power
    if (vesselConfig.autoRequiredGeneralAlarm && currentOptional.includes('general_alarm')) {
      // Only remove if not required for other reasons (length, max persons)
      const maxPersons = parseInt(vesselConfig.maxPersons) || 0;
      const length = parseFloat(vesselConfig.lengthOverall) || 0;
      
      if (maxPersons < 16 && length < 24) {
        currentOptional = currentOptional.filter(eq => eq !== 'general_alarm');
        updates.autoRequiredGeneralAlarm = false;
      }
    }
  }
  
  // Update optional equipment if changed
  if (currentOptional.length !== (vesselConfig.optionalEquipment || []).length ||
      currentOptional.some((eq, idx) => eq !== (vesselConfig.optionalEquipment || [])[idx])) {
    updates.optionalEquipment = currentOptional;
  }
  
  updateConfig(updates);
};

export const handleMaxPersonsChange = (maxPersons, vesselConfig, updateConfig) => {
  const updates = { maxPersons };
  let currentOptional = [...(vesselConfig.optionalEquipment || [])];
  const persons = parseInt(maxPersons) || 0;
  
  // Handle General Alarm based on max persons
  if (persons >= 16) {
    // Auto-require general alarm for vessels with ≥16 persons
    if (!currentOptional.includes('general_alarm')) {
      currentOptional.push('general_alarm');
      updates.autoRequiredGeneralAlarm = true;
    }
  } else {
    // Remove GA if it was auto-added for max persons
    if (vesselConfig.autoRequiredGeneralAlarm && currentOptional.includes('general_alarm')) {
      // Only remove if not required for other reasons (length, power)
      const enginePower = parseFloat(vesselConfig.enginePower) || 0;
      const length = parseFloat(vesselConfig.lengthOverall) || 0;
      
      if (enginePower <= 750 && length < 24) {
        currentOptional = currentOptional.filter(eq => eq !== 'general_alarm');
        updates.autoRequiredGeneralAlarm = false;
      }
    }
  }
  
  // Update optional equipment if changed
  if (currentOptional.length !== (vesselConfig.optionalEquipment || []).length ||
      currentOptional.some((eq, idx) => eq !== (vesselConfig.optionalEquipment || [])[idx])) {
    updates.optionalEquipment = currentOptional;
  }
  
  updateConfig(updates);
};

export const handleVesselTypeChange = (vesselType, vesselConfig, updateConfig) => {
  const updates = { vesselType };
  let currentOptional = [...(vesselConfig.optionalEquipment || [])];
  const preset = vesselTypePresets[vesselType];
  
  // Handle pilot boat specific requirements per WBC3 27.2.3 Table 8.6
  if (vesselType === 'pilot' || vesselType === 'pilot_boat_dedicated' || vesselType === 'workboat_pilot_endorsed') {
    // Pilot boats cannot have petrol engines - switch to diesel if currently petrol
    if (vesselConfig.engineType === 'petrol' || vesselConfig.engineType === 'inboard_petrol') {
      updates.engineType = 'diesel';
      updates.petrolEngineWarningShown = true;
      // Show warning to user
      if (typeof window !== 'undefined' && window.alert) {
        window.alert('⚠️ Pilot boats cannot be fitted with petrol engines per WBC3 27.2.3 Table 8.6.\n\nEngine type has been automatically changed to diesel.');
      }
    }
  }
  
  if (preset) {
    Object.keys(preset).forEach(key => {
      if (key !== 'mandatoryEquipment' && key !== 'specialEquipment' && key !== 'operations') {
        updates[key] = preset[key];
      }
    });
    
    updates.operations = preset.operations || [];
    updates.specialEquipment = preset.specialEquipment || [];
    
    if (preset.mandatoryEquipment && preset.mandatoryEquipment.length > 0) {
      preset.mandatoryEquipment.forEach(eq => {
        if (!currentOptional.includes(eq)) {
          currentOptional.push(eq);
        }
      });
      updates.vesselTypeMandatoryEquipment = preset.mandatoryEquipment;
    }
  } else {
    if (vesselType === 'multipurpose') {
      updates.operations = [];
    } else if (vesselTypeOperations[vesselType]) {
      updates.operations = vesselTypeOperations[vesselType];
    } else {
      updates.operations = [];
    }
  }
  
  if (vesselType === 'police' || vesselType === 'police_boat') {
    updates.policeBoat = true;
    
    // WBC3 Annex 3 requirements only apply to seagoing police boats
    if (vesselConfig.operatingStatus === 'seagoing') {
      updates.enhancedLSARequired = true;
      // Note: Immersion suits for ALL persons will be handled in equipment requirements
      // Note: No petrol engines allowed will be enforced in engine type selection
    } else if (vesselConfig.operatingStatus === 'inland') {
      // Inland police boats have enhanced safety but not WBC3 Annex 3
      updates.enhancedLSARequired = true; // Still enhanced LSA for safety
      // But no immersion suits requirement for inland waters
    }
  }
  
  // Passenger vessels ALWAYS require PA system (for safety announcements)
  // Note: This is different from General Alarm which has specific thresholds
  if (vesselType === 'pax') {
    // PA system is mandatory for ALL passenger vessels
    if (!currentOptional.includes('pa_system')) {
      currentOptional.push('pa_system');
    }
    updates.autoRequiredPASystem = true;
    
    // Also flag that passenger capacity is required
    updates.requiresPassengerInput = true;
    updates.passengerInputReminder = 'Please enter the passenger capacity for this vessel';
  } else if (vesselType === 'ctv') {
    // CTVs carry personnel so need PA system
    if (!currentOptional.includes('pa_system')) {
      currentOptional.push('pa_system');
    }
    updates.autoRequiredPASystem = true;
    
    updates.requiresPassengerInput = true;
    updates.passengerInputReminder = 'Please enter the number of technicians/personnel this CTV can carry';
  } else {
    // Remove PA system if switching away from passenger vessel
    // (unless required for other reasons)
    if (vesselConfig.autoRequiredPASystem) {
      const maxPersons = parseInt(vesselConfig.maxPersons) || 0;
      const enginePower = parseFloat(vesselConfig.enginePower) || 0;
      const passengers = parseInt(vesselConfig.passengerCapacity) || 0;
      
      // Only remove if not required for general alarm
      if (maxPersons < 16 && enginePower <= 750 && passengers === 0) {
        currentOptional = currentOptional.filter(eq => eq !== 'pa_system');
        updates.autoRequiredPASystem = false;
      }
    }
    
    updates.requiresPassengerInput = false;
    updates.passengerInputReminder = null;
  }
  
  // Check if we need general alarm (separate from PA system)
  // GA required for ≥16 persons OR >750kW OR ≥24m length
  const lengthOverall = parseFloat(vesselConfig.lengthOverall) || 0;
  if (lengthOverall >= 24) {
    if (!currentOptional.includes('general_alarm')) {
      currentOptional.push('general_alarm');
      updates.autoRequiredGeneralAlarm = true;
    }
  }
  
  // Update optional equipment if changed
  if (currentOptional.length !== (vesselConfig.optionalEquipment || []).length ||
      currentOptional.some((eq, idx) => eq !== (vesselConfig.optionalEquipment || [])[idx])) {
    updates.optionalEquipment = currentOptional;
  }
  
  updateConfig(updates);
};

export const handleLengthChange = (lengthOverall, vesselConfig, updateConfig) => {
  const updates = { lengthOverall };
  let currentOptional = [...(vesselConfig.optionalEquipment || [])];
  const length = parseFloat(lengthOverall) || 0;
  
  // General Alarm required for vessels ≥24m
  if (length >= 24) {
    if (!currentOptional.includes('general_alarm')) {
      currentOptional.push('general_alarm');
      updates.autoRequiredGeneralAlarm = true;
    }
  } else {
    // Remove GA if it was auto-added for length
    if (vesselConfig.autoRequiredGeneralAlarm && currentOptional.includes('general_alarm')) {
      // Only remove if not required for other reasons
      const maxPersons = parseInt(vesselConfig.maxPersons) || 0;
      const enginePower = parseFloat(vesselConfig.enginePower) || 0;
      
      // Don't remove if required for persons or power
      if (maxPersons < 16 && enginePower <= 750) {
        currentOptional = currentOptional.filter(eq => eq !== 'general_alarm');
        updates.autoRequiredGeneralAlarm = false;
      }
    }
  }
  
  // Update optional equipment if changed
  if (currentOptional.length !== (vesselConfig.optionalEquipment || []).length ||
      currentOptional.some((eq, idx) => eq !== (vesselConfig.optionalEquipment || [])[idx])) {
    updates.optionalEquipment = currentOptional;
  }
  
  updateConfig(updates);
};

export const handleMaxSpeedChange = (maxSpeed, vesselConfig, updateConfig) => {
  const updates = { maxSpeed };
  const speed = parseFloat(maxSpeed) || 0;
  
  if (speed >= 20) {
    updates.highSpeedVessel = true;
    updates.highSpeedRequirements = {
      vibrationAssessment: true,
      riskAssessment: true,
      inboardSeating: true,
      seatedOperation: true
    };
  } else {
    updates.highSpeedVessel = false;
    updates.highSpeedRequirements = null;
  }
  
  updateConfig(updates);
};

export const handleCategoryChange = (category, vesselConfig, updateConfig) => {
  const updates = { category };
  const mandatoryEquipment = [];
  const categoryNum = parseCategory(category);
  
  if (!isNaN(categoryNum)) {
    // Area 0-2 (Offshore)
    if (categoryNum <= 2) {
      mandatoryEquipment.push('radar', 'navtex', 'epirb', 'sart');
      if (categoryNum <= 1) {
        mandatoryEquipment.push('mf_radio'); // Area 0-1 only
      }
    }
    // Area 0-4 require GPS and Echo Sounder
    if (categoryNum <= 4) {
      mandatoryEquipment.push('gps', 'echo_sounder');
    }
    
    // AIS requirements (≥300 GT or passenger vessels)
    const tonnage = parseFloat(vesselConfig.grossTonnage) || 0;
    const passengers = parseInt(vesselConfig.passengerCapacity) || 0;
    if (tonnage >= 300 || passengers > 0) {
      mandatoryEquipment.push('ais');
    }
  }
  
  // Store auto-required equipment (don't overwrite user selections)
  updates.autoRequiredEquipment = mandatoryEquipment;
  
  updateConfig(updates);
};